import React, { useState } from 'react';
import { Save, Info } from 'react-feather';
import { API_BASE_URL } from '../../config/api';

const AutoReloadModal = ({ currentSettings, onClose, onSave }) => {
    const [enabled, setEnabled] = useState(Boolean(currentSettings?.enabled));
    const [threshold, setThreshold] = useState(currentSettings?.threshold ?? 10.00);
    const [amount, setAmount] = useState(currentSettings?.amount ?? 20.00);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const parsedThreshold = parseFloat(threshold);
            const parsedAmount = parseFloat(amount);
            const safeThreshold = Number.isFinite(parsedThreshold) ? parsedThreshold : 10;
            const safeAmount = Number.isFinite(parsedAmount) ? parsedAmount : 20;

            if (enabled && (!Number.isFinite(parsedThreshold) || parsedThreshold < 1 || !Number.isFinite(parsedAmount) || parsedAmount < 5)) {
                throw new Error('Please enter valid threshold and reload amounts.');
            }

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/api/payment/auto-reload`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    enabled,
                    threshold: safeThreshold,
                    amount: safeAmount
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Failed to update auto-reload settings');
            }

            onSave();
        } catch (err) {
            setError(err.message || 'Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    const fmtUSD = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

    return (
        <form onSubmit={handleSubmit} className="auto-reload-form">
            {error && <div className="auto-reload-error" role="alert">{error}</div>}

            {/* Toggle row */}
            <div className="auto-reload-toggle-row">
                <div className="auto-reload-toggle-label">
                    <label htmlFor="auto-reload-toggle">Enable Auto-Reload</label>
                    <small>Automatically add funds when your balance gets low.</small>
                </div>
                <div className="auto-reload-toggle-control">
                    <span className={`auto-reload-toggle-badge ${enabled ? 'is-on' : 'is-off'}`}>
                        {enabled ? 'ON' : 'OFF'}
                    </span>
                    <button
                        id="auto-reload-toggle"
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        className={`auto-reload-switch ${enabled ? 'is-enabled' : ''}`}
                        onClick={() => setEnabled(!enabled)}
                        aria-label={enabled ? 'Disable auto-reload' : 'Enable auto-reload'}
                    >
                        <span className="auto-reload-switch-thumb" />
                    </button>
                </div>
            </div>

            {/* Input fields */}
            <div className={`auto-reload-fields ${!enabled ? 'is-disabled' : ''}`}>
                <div className="auto-reload-field">
                    <label htmlFor="auto-reload-threshold">Minimum Balance Threshold ($)</label>
                    <input
                        id="auto-reload-threshold"
                        type="number"
                        min="1"
                        step="0.01"
                        value={threshold}
                        onChange={(e) => setThreshold(e.target.value)}
                        disabled={!enabled}
                        required={enabled}
                        className="auto-reload-input"
                        placeholder="5"
                    />
                    <small className="auto-reload-help">We will reload your account when it drops below this amount.</small>
                </div>

                <div className="auto-reload-field">
                    <label htmlFor="auto-reload-amount">Reload Amount ($)</label>
                    <input
                        id="auto-reload-amount"
                        type="number"
                        min="5"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={!enabled}
                        required={enabled}
                        className="auto-reload-input"
                        placeholder="20"
                    />
                    <small className="auto-reload-help">The amount to charge your card.</small>
                </div>
            </div>

            {/* Live preview, only when enabled */}
            {enabled && (
                <div className="auto-reload-preview">
                    <Info size={14} className="auto-reload-preview-icon" />
                    <span>
                        When your balance drops below <strong>{fmtUSD(threshold)}</strong>,
                        we'll automatically reload <strong>{fmtUSD(amount)}</strong> to your account.
                    </span>
                </div>
            )}

            <div className="auto-reload-actions">
                <button type="button" className="auto-reload-btn-cancel" onClick={onClose} disabled={loading}>
                    Cancel
                </button>
                <button type="submit" className="auto-reload-btn-save" disabled={loading}>
                    {loading ? 'Saving...' : <><Save size={18} /> Save Settings</>}
                </button>
            </div>
        </form>
    );
};

export default AutoReloadModal;
